"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { AiMarkerKind } from "@repo/ai-disclaimer";
import type { InventoryRow } from "@repo/utils/aiImagesInventory";
import type { AiIconBindings as Bindings } from "@repo/utils/aiDisclaimerIcons";
import AiIconBindings from "./AiIconBindings";
import AiImagePreview from "./AiImagePreview";
import AiImagesHistory from "./AiImagesHistory";
import AiImagesRow from "./AiImagesRow";
import type {
  AiAssetMarkerResponse,
  AiBadgePositionResponse,
  AiBadgeSizeResponse,
  AiImagesBulkResponse,
  AiImagesInventoryResponse,
} from "@/types/aiImages";
import {
  AI_BADGE_POSITIONS,
  AI_BADGE_POSITION_LABEL,
  type AiBadgePosition,
} from "@repo/utils/aiBadgePosition";
import {
  AI_BADGE_SIZES,
  AI_BADGE_SIZE_LABEL,
  isAiBadgeSize,
  type AiBadgeSize,
} from "@repo/utils/aiBadgeSize";
import { compactBadgeSize } from "@repo/utils/aiIconVariant";
import {
  AdminButton,
  AdminCheckbox,
  AdminField,
  AdminInput,
  AdminNotice,
  AdminSelect,
  AdminSpinner,
  AdminStat,
} from "./AdminUi";
import "./aiImages.css";

type Filter = "unmarked" | "marked" | "all";
type PageSize = 25 | 50 | 100 | "all";

const PAGE_SIZES: PageSize[] = [25, 50, 100, "all"];
const DEFAULT_PAGE_SIZE: PageSize = 25;

const KIND_OPTIONS: { value: AiMarkerKind; label: string }[] = [
  { value: "generated", label: "AI-generated" },
  { value: "modified", label: "AI-edited" },
  { value: "unspecified", label: "AI (unspecified)" },
];

/** What to put in a notice when a write throws rather than answering. */
function describeError(error: unknown): string {
  return error instanceof Error ? error.message : "the request failed";
}

/**
 * The inventory with one row's marker updated, counts included.
 *
 * Patched locally rather than re-read, because re-reading costs the full asset scan the
 * single-asset route exists to avoid. Only the fields that can move are touched: the
 * row's own marker fields, the marked/unmarked totals, and the registry size the drift
 * warning is measured against.
 */
function applyRowMarker(
  data: AiImagesInventoryResponse,
  saved: AiAssetMarkerResponse,
): AiImagesInventoryResponse {
  const patch = (rows: InventoryRow[]) =>
    rows.map((row) =>
      row.id === saved.id
        ? {
            ...row,
            rawAlt: saved.rawAlt,
            cleanAlt: saved.cleanAlt,
            isAi: saved.isAi,
            kind: saved.kind,
          }
        : row,
    );

  const previous = [...data.images, ...data.videos].find(
    (row) => row.id === saved.id,
  );
  const was = previous?.isAi && previous.kind ? previous.kind : null;
  const now = saved.isAi ? saved.kind : null;

  const byKind = { ...data.counts.byKind };

  if (was) byKind[was] -= 1;
  if (now) byKind[now] += 1;

  const markedDelta = (now ? 1 : 0) - (was ? 1 : 0);

  return {
    ...data,
    images: patch(data.images),
    videos: patch(data.videos),
    counts: {
      ...data.counts,
      byKind,
      marked: data.counts.marked + markedDelta,
      unmarked: data.counts.unmarked - markedDelta,
    },
    // The server's own count where it has one; a failed registry write leaves the
    // previous size standing, which is what the drift warning should then be comparing.
    registrySize: saved.registrySize ?? data.registrySize,
    // A cleared marker takes its corner override with it — the registry row is gone.
    assetBadgePositions: saved.isAi
      ? data.assetBadgePositions
      : Object.fromEntries(
          Object.entries(data.assetBadgePositions).filter(
            ([id]) => Number(id) !== saved.id,
          ),
        ),
  };
}

const AiImagesConfig = () => {
  const [data, setData] = useState<AiImagesInventoryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>("unmarked");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [kind, setKind] = useState<AiMarkerKind>("generated");
  const [bulkResult, setBulkResult] = useState<AiImagesBulkResponse | null>(
    null,
  );
  const [bulkAction, setBulkAction] = useState<"apply" | "remove">("apply");
  const [writeNotice, setWriteNotice] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);
  const [preview, setPreview] = useState<InventoryRow | null>(null);
  /**
   * The one write in flight, if any, identified for the spinner beside its control.
   *
   * A single page-level lock rather than a set of per-id locks. Every write on this page
   * — a row marker, a row corner, a bulk run, the site default — is a read-modify-write
   * of the SAME config story, so two of them overlapping is not a per-row hazard: the
   * second reads before the first has written, then writes its own copy back over it,
   * and the first edit is gone from the registry with nothing to say so. Serialising
   * them at the page is the only guard the client can offer; the server has no
   * compare-and-set to lean on (Storyblok's Management API takes no version precondition
   * on a story update).
   */
  const [writing, setWriting] = useState<
    | { scope: "marker" | "position"; assetId: number }
    | { scope: "bulk" | "site" | "size" }
    | null
  >(null);
  // The corner the "set on selected" action will write. Separate state from the site
  // default precisely because the two were confusable: one select that meant different
  // things depending on whether anything was ticked is how a mis-click moved every badge.
  // "" is "leave it alone", NOT "use the site default". An Apply run that also cleared
  // every override on the selection would undo placement work an editor did earlier and
  // never asked to touch, which is the same class of surprise as the old toolbar select.
  const [selectionPosition, setSelectionPosition] = useState<
    AiBadgePosition | "" | "default"
  >("");

  const isWriting = writing !== null;
  const isBulkRunning = writing?.scope === "bulk";
  const isSavingSitePosition = writing?.scope === "site";
  const isSavingSiteSize = writing?.scope === "size";

  /**
   * Re-reads the inventory.
   *
   * `silent` keeps the table on screen while it refreshes. A bulk run has to resync the
   * counts and the registry size, and swapping the whole page for "Loading asset
   * inventory…" would throw the editor back to the top of a 700-row table each time.
   */

  const load = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);

    try {
      // `no-store`: this is read-after-write state. A cached inventory would show a marker
      // that has already been removed, which reads as the removal having failed.
      const response = await fetch("/api/ai-images", { cache: "no-store" });

      if (!response.ok) {
        throw new Error(`Inventory request failed (${response.status})`);
      }

      setData((await response.json()) as AiImagesInventoryResponse);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Failed to load inventory",
      );
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Everything a badge can render over, images and videos together.
   *
   * One list rather than two tables: marking, positioning and the registry treat them
   * identically, and splitting the view would make "is everything labelled?" a question
   * an editor has to ask twice.
   */
  const markable = useMemo(
    () => (data ? [...data.images, ...data.videos] : []),
    [data],
  );

  const visible = useMemo(() => {
    if (!data) return [];

    const term = search.trim().toLowerCase();

    return markable.filter((row) => {
      if (filter === "marked" && !row.isAi) return false;
      if (filter === "unmarked" && row.isAi) return false;
      if (!term) return true;

      return (
        row.filename.toLowerCase().includes(term) ||
        row.cleanAlt.toLowerCase().includes(term)
      );
    });
  }, [data, markable, filter, search]);

  // The page index is clamped rather than reset on every list change: reloading after a bulk
  // write can shrink the list under the reader's feet, and clamping keeps them on the last
  // page instead of throwing them back to the first.
  const pageCount =
    pageSize === "all" ? 1 : Math.max(1, Math.ceil(visible.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const pageStart = pageSize === "all" ? 0 : currentPage * pageSize;
  const paged =
    pageSize === "all"
      ? visible
      : visible.slice(pageStart, pageStart + pageSize);

  const allOnPageSelected =
    paged.length > 0 && paged.every((row) => selected.has(row.id));
  const someOnPageSelected =
    !allOnPageSelected && paged.some((row) => selected.has(row.id));

  const toggle = useCallback((id: number) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /**
   * Adds or removes a batch of ids without touching the rest of the selection.
   *
   * Select-all is scoped to what the editor can currently see, so it composes: paging
   * through and selecting each page builds one selection, rather than each page silently
   * replacing the last.
   */
  const setMany = useCallback((rows: InventoryRow[], next: boolean) => {
    setSelected((current) => {
      const updated = new Set(current);

      for (const row of rows) {
        if (next) updated.add(row.id);
        else updated.delete(row.id);
      }

      return updated;
    });
  }, []);

  /**
   * Moves the badge for the whole site.
   *
   * The local state is updated from the response rather than optimistically: the write
   * goes to the CMS and a failure must leave the control showing what is actually stored,
   * not what was clicked.
   */
  const saveSitePosition = async (position: AiBadgePosition) => {
    if (isWriting) return;

    setWriteNotice(null);
    setWriting({ scope: "site" });

    try {
      const response = await fetch("/api/ai-images/position", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ position }),
      });

      if (!response.ok) {
        setWriteNotice(
          `Could not move the badge site-wide (${response.status}).`,
        );
        return;
      }

      const saved = (await response.json()) as AiBadgePositionResponse;

      setData((current) =>
        current && saved.position
          ? { ...current, badgePosition: saved.position }
          : current,
      );
    } catch (error) {
      // Caught, not left to an unhandled rejection: a dropped connection or an HTML 502
      // from a proxy throws out of `fetch` or `.json()`, and without this the spinner
      // simply vanishes and the control snaps back with nothing said.
      setWriteNotice(
        `Could not move the badge site-wide: ${describeError(error)}`,
      );
    } finally {
      setWriting(null);
    }
  };

  /**
   * Resizes the badge for the whole site.
   *
   * Same shape as `saveSitePosition`, and for the same reason the state follows the
   * response: the control must show what the CMS stored, not what was clicked.
   */
  const saveSiteSize = async (size: AiBadgeSize) => {
    if (isWriting) return;

    setWriteNotice(null);
    setWriting({ scope: "size" });

    try {
      const response = await fetch("/api/ai-images/size", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ size }),
      });

      if (!response.ok) {
        setWriteNotice(`Could not resize the badge (${response.status}).`);
        return;
      }

      const saved = (await response.json()) as AiBadgeSizeResponse;

      setData((current) =>
        current && isAiBadgeSize(saved.size)
          ? { ...current, badgeSize: saved.size }
          : current,
      );
    } catch (error) {
      setWriteNotice(`Could not resize the badge: ${describeError(error)}`);
    } finally {
      setWriting(null);
    }
  };

  /**
   * Sets or clears the marker on one asset, straight from its row.
   *
   * Uses the single-asset route rather than the bulk one. The bulk route scans the whole
   * library to find its targets and to rebuild the registry, so a dropdown wired to it
   * cost two full scans per row — twenty edits would be forty scans against a
   * rate-limited API, and on a space large enough to truncate the scan the rebuild is
   * refused outright, leaving every row edit writing an alt and skipping the registry.
   *
   * The response carries the row back, so the table patches that one row instead of
   * re-reading the inventory.
   */
  const saveRowMarker = async (assetId: number, kind: AiMarkerKind | null) => {
    if (isWriting) return;

    setWriteNotice(null);
    setWriting({ scope: "marker", assetId });

    try {
      const response = await fetch("/api/ai-images/asset", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assetId, kind }),
      });

      if (!response.ok) {
        setWriteNotice(
          `Could not change the marker for asset ${assetId} (${response.status}).`,
        );
        return;
      }

      const saved = (await response.json()) as AiAssetMarkerResponse;

      if (saved.registryError) {
        // The alt landed but the registry did not, so the badge on the site still
        // follows the previous state. Silence here would look like a clean success.
        setWriteNotice(
          `The marker was written but the registry was not updated (${saved.registryError}) — badges on the site are unchanged.`,
        );
      } else if (saved.logError) {
        setWriteNotice(
          `The marker was written but the run was not logged (${saved.logError}) — it cannot be reverted from the history panel.`,
        );
      }

      setData((current) =>
        current ? applyRowMarker(current, saved) : current,
      );
    } catch (error) {
      setWriteNotice(
        `Could not change the marker for asset ${assetId}: ${describeError(error)}`,
      );
    } finally {
      setWriting(null);
    }
  };

  /** Sets or clears one asset's corner. `null` returns it to the site default. */
  const saveAssetPosition = async (
    assetId: number,
    position: AiBadgePosition | null,
  ) => {
    if (isWriting) return;

    setWriteNotice(null);
    setWriting({ scope: "position", assetId });

    try {
      const response = await fetch("/api/ai-images/position", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assetIds: [assetId], position }),
      });

      if (!response.ok) {
        setWriteNotice(
          `Could not move the badge for asset ${assetId} (${response.status}).`,
        );
        return;
      }

      setData((current) => {
        if (!current) return current;

        const next = { ...current.assetBadgePositions };

        if (position) next[assetId] = position;
        else delete next[assetId];

        return { ...current, assetBadgePositions: next };
      });
    } catch (error) {
      setWriteNotice(
        `Could not move the badge for asset ${assetId}: ${describeError(error)}`,
      );
    } finally {
      setWriting(null);
    }
  };

  /**
   * Applies or removes the marker across the selection, and — on an apply — the corner
   * too when one is chosen.
   *
   * One button rather than two: marking an asset and deciding where its badge sits is a
   * single editorial act, and a second button next to this one is what made the earlier
   * position control look like it acted on the selection when it did not.
   *
   * The position is written after the marker, never before: the registry row a corner
   * attaches to does not exist until the marker has been applied, so the reverse order
   * would silently skip every newly-marked asset.
   */
  const runBulk = async (action: "apply" | "remove") => {
    // Guards the double-click, and every other write on the page: the button is disabled
    // too, but a second click can land in the frame before React re-renders, and a
    // duplicate run reports every write the first one made as "already carries this
    // marker" — which reads as a failure.
    if (isWriting) return;

    const assetIds = [...selected];

    setWriting({ scope: "bulk" });
    setBulkResult(null);
    setBulkAction(action);
    setWriteNotice(null);

    try {
      const response = await fetch("/api/ai-images", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assetIds, action, kind }),
      });

      if (!response.ok && response.status !== 207) {
        setWriteNotice(`Bulk request failed (${response.status}).`);
        return;
      }

      const result = (await response.json()) as AiImagesBulkResponse;

      setBulkResult(result);

      if (action === "apply" && selectionPosition) {
        // Only the assets whose marker actually landed can take a corner: a failed
        // marker write leaves no registry row, and the position route rightly skips it.
        // Asking for the ones that failed would turn a partial success into a 409 whose
        // message blames the position — see the `missing` handling below.
        const positionable = assetIds.filter(
          (id) => !result.failed.some((failure) => failure.id === id),
        );

        if (result.registryError) {
          // The corner attaches to a registry row the rebuild did not write, so there is
          // nothing to attach it to. Said plainly rather than attempted and reported as
          // a position failure, which would name the wrong cause.
          setWriteNotice(
            `The markers were applied, but the registry was not rebuilt (${result.registryError}), so the badge position was not changed.`,
          );
        } else if (positionable.length === 0) {
          setWriteNotice(
            "No markers landed, so the badge position was not changed.",
          );
        } else {
          const positionResponse = await fetch("/api/ai-images/position", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              assetIds: positionable,
              // "default" is the explicit "clear the override" choice; "" never gets here.
              position:
                selectionPosition === "default" ? null : selectionPosition,
            }),
          });

          if (!positionResponse.ok && positionResponse.status !== 207) {
            // Reported on its own: the markers did land, and saying nothing would leave
            // an editor believing the badges moved with them.
            setWriteNotice(
              `The markers were applied, but the badge position was not (${positionResponse.status}).`,
            );
          } else {
            const saved =
              (await positionResponse.json()) as AiBadgePositionResponse;

            // A 207 is a partial write. Without naming the ids, an editor reads "done"
            // and believes every badge moved.
            if (saved.missing?.length) {
              setWriteNotice(
                `The badge position was not changed on ${saved.missing.length} asset(s) — they are not in the registry: ${saved.missing.join(", ")}.`,
              );
            }
          }
        }
      }

      setSelected(new Set());
      await load(true);
    } catch (error) {
      setWriteNotice(`The bulk run failed: ${describeError(error)}`);
    } finally {
      setWriting(null);
    }
  };

  if (isLoading)
    return (
      <div className="ai-admin p-6 text-[var(--admin-text-muted)]">
        Loading asset inventory…
      </div>
    );
  if (error)
    return (
      <div className="ai-admin p-6">
        <AdminNotice tone="danger">{error}</AdminNotice>
      </div>
    );
  if (!data) return null;

  const { counts } = data;
  // A mismatch means a run wrote the assets but not the registry — badges are then
  // missing on every placement whose story field never copied a marker.
  const registryDrift = counts.marked !== data.registrySize;

  return (
    <div className="ai-admin bg-[var(--admin-surface)] p-6">
      <h1
        className="mb-1 text-3xl font-bold"
        style={{ fontFamily: "var(--admin-font-display)" }}
      >
        AI image labelling
      </h1>
      <p className="mb-6 text-sm text-[var(--admin-text-muted)]">
        Marker syntax is appended to an asset&apos;s alt text. Readers never see
        it — it is stripped before render and drives the disclaimer badge.
      </p>

      <dl className="mb-6 flex flex-wrap gap-8">
        <AdminStat label="Images" value={counts.images} />
        <AdminStat label="Videos" value={counts.videos} />
        <AdminStat label="Marked" value={counts.marked} />
        <AdminStat label="Unmarked" value={counts.unmarked} />
        <AdminStat label="Other assets" value={counts.nonImages} />
        {counts.unknown > 0 && (
          <AdminStat label="Unknown type" value={counts.unknown} />
        )}
        <AdminStat
          label="In registry"
          value={data.registrySize}
          emphasis={registryDrift}
        />
      </dl>

      {data.truncated && (
        <AdminNotice
          tone="danger"
          className="mb-4"
          data-testid="scan-truncated"
        >
          <strong>
            The asset scan hit its page limit, so this is a partial library.
          </strong>{" "}
          Every count above understates the space, an image past the limit
          cannot be selected here, and the registry will not be rewritten while
          this is true — rebuilding it from a partial list would unlabel
          everything it could not see. Raise <code>MAX_PAGES</code> in{" "}
          <code>lib/aiImagesRegistry.ts</code>.
        </AdminNotice>
      )}

      {registryDrift && (
        <AdminNotice
          tone="attention"
          className="mb-4"
          data-testid="registry-drift"
        >
          <strong>
            {counts.marked} assets are marked, but the registry lists{" "}
            {data.registrySize}.
          </strong>{" "}
          Badges appear on a page either because the asset&apos;s own alt text
          carries the marker, or because this registry does. Where neither is
          true the asset is labelled here and unlabelled on the site. Re-running
          apply or remove on any asset rewrites the registry from scratch and
          clears this.
        </AdminNotice>
      )}

      {data.bynderBlindSpot && (
        <AdminNotice
          tone="attention"
          className="mb-4"
          data-testid="bynder-blind-spot"
        >
          <strong>Bynder images are not listed here.</strong> This inventory
          scans Storyblok asset records, and a Bynder-hosted image has none — so
          it cannot appear in these counts even if it is AI-generated.
          Bynder-sourced images still get their alt text (and therefore their
          marker and badge) from Storyblok, so they can be labelled by editing
          the field that references them.
        </AdminNotice>
      )}

      <p className="mb-4 text-sm text-[var(--admin-text-muted)]">
        {counts.nonImages} assets (PDFs, archives) are excluded — a badge can
        only render over an image or a video.
      </p>

      {/* Its own row, deliberately outside the toolbar below. Sitting beside the
          selection controls, this read as "the corner for the ticked assets" and was in
          fact the site-wide default — one mis-click moved every badge on the site. */}
      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-[var(--admin-radius)] border border-[var(--admin-border)] p-3">
        <AdminField label="Default badge position (every asset)">
          <AdminSelect
            value={data.badgePosition}
            disabled={isWriting}
            onChange={(event) =>
              void saveSitePosition(event.target.value as AiBadgePosition)
            }
          >
            {AI_BADGE_POSITIONS.map((position) => (
              <option key={position} value={position}>
                {AI_BADGE_POSITION_LABEL[position]}
              </option>
            ))}
          </AdminSelect>
        </AdminField>
        {isSavingSitePosition && <AdminSpinner />}
        <AdminField label="Badge size (every asset)">
          <AdminSelect
            value={data.badgeSize}
            disabled={isWriting}
            onChange={(event) => {
              const size = Number(event.target.value);
              if (isAiBadgeSize(size)) void saveSiteSize(size);
            }}
          >
            {AI_BADGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {AI_BADGE_SIZE_LABEL[size]}
              </option>
            ))}
          </AdminSelect>
        </AdminField>
        {isSavingSiteSize && <AdminSpinner />}
        <p className="text-sm text-[var(--admin-text-muted)]">
          Both apply to every AI-labelled asset on the site; size is the badge
          height in px (small list thumbnails get a{" "}
          {compactBadgeSize(data.badgeSize)}px badge at this size). To move one
          asset only, use the <strong>Badge position</strong> column in the
          table below; to move several, tick them and set{" "}
          <strong>Badge position (on apply)</strong> before applying.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-[var(--admin-radius)] border border-[var(--admin-border)] bg-[var(--admin-surface-muted)] p-3">
        <AdminField label="Show">
          <AdminSelect
            value={filter}
            onChange={(event) => {
              setFilter(event.target.value as Filter);
              setPage(0);
            }}
          >
            <option value="unmarked">Unmarked ({counts.unmarked})</option>
            <option value="marked">Marked ({counts.marked})</option>
            <option value="all">All assets ({markable.length})</option>
          </AdminSelect>
        </AdminField>

        <AdminField label="Search">
          <AdminInput
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            placeholder="filename or alt text"
          />
        </AdminField>

        <AdminField label="Per page">
          <AdminSelect
            value={String(pageSize)}
            onChange={(event) => {
              const value = event.target.value;
              setPageSize(
                value === "all" ? "all" : (Number(value) as PageSize),
              );
              setPage(0);
            }}
          >
            {PAGE_SIZES.map((size) => (
              <option key={String(size)} value={String(size)}>
                {size === "all" ? "All" : size}
              </option>
            ))}
          </AdminSelect>
        </AdminField>

        <AdminField label="Marker">
          <AdminSelect
            value={kind}
            disabled={isWriting}
            onChange={(event) => setKind(event.target.value as AiMarkerKind)}
          >
            {KIND_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </AdminSelect>
        </AdminField>

        <AiIconBindings
          kind={kind}
          bindings={data.icons}
          assets={data.images}
          onSaved={(icons: Bindings) =>
            setData((current) => (current ? { ...current, icons } : current))
          }
        />

        <AdminField label="Badge position (on apply)">
          <AdminSelect
            value={selectionPosition}
            disabled={isWriting}
            onChange={(event) =>
              setSelectionPosition(
                event.target.value as AiBadgePosition | "" | "default",
              )
            }
          >
            <option value="">Leave unchanged</option>
            <option value="default">
              Site default ({AI_BADGE_POSITION_LABEL[data.badgePosition]})
            </option>
            {AI_BADGE_POSITIONS.map((position) => (
              <option key={position} value={position}>
                {AI_BADGE_POSITION_LABEL[position]}
              </option>
            ))}
          </AdminSelect>
        </AdminField>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {isBulkRunning && <AdminSpinner />}
          <AdminButton
            variant="primary"
            disabled={selected.size === 0 || isWriting}
            onClick={() => void runBulk("apply")}
          >
            Apply to {selected.size} selected
          </AdminButton>
          <AdminButton
            disabled={selected.size === 0 || isWriting}
            onClick={() => void runBulk("remove")}
          >
            Remove from {selected.size} selected
          </AdminButton>
        </div>
      </div>

      <AiImagesHistory onReverted={() => void load()} />

      {writeNotice && (
        <AdminNotice tone="info" className="mb-4">
          {writeNotice}
        </AdminNotice>
      )}

      {bulkResult && (
        <AdminNotice tone="confirm" className="mb-4">
          {bulkResult.succeeded.length} updated
          {bulkResult.skipped.length > 0 &&
            // Naming the reason rather than "already correct": on a remove, a skip means
            // there was no marker to strip, which is the exact thing a reader looking at a
            // marker-still-showing row needs to be told apart from a failed write.
            `, ${bulkResult.skipped.length} unchanged (${
              bulkAction === "remove"
                ? "no marker to remove"
                : "already carries this marker"
            })`}
          {bulkResult.registryError && (
            <>
              {" "}
              <strong>
                The registry was not rewritten ({bulkResult.registryError}) —
                badges on the site still follow the previous run.
              </strong>
            </>
          )}
          {bulkResult.logError && (
            <>
              {" "}
              <strong>
                The run was not logged ({bulkResult.logError}) — it cannot be
                reverted from the history panel.
              </strong>
            </>
          )}
          {bulkResult.failed.length > 0 && (
            <>
              {", "}
              <strong>{bulkResult.failed.length} failed</strong>:{" "}
              {bulkResult.failed.map((f) => `${f.id} (${f.reason})`).join("; ")}
            </>
          )}
          .
        </AdminNotice>
      )}

      <p className="mb-2 text-sm text-[var(--admin-text-muted)]">
        Disclaimer copy in use (version {data.disclaimerVersion}):{" "}
        {Object.entries(data.disclaimers)
          .map(([k, copy]) => `${k} — “${copy}”`)
          .join(" · ")}
      </p>

      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-b-2 border-[var(--admin-border-strong)] text-xs tracking-wide text-[var(--admin-text-muted)] uppercase">
            <th className="p-2">
              <AdminCheckbox
                checked={allOnPageSelected}
                ref={(node) => {
                  // Indeterminate is not an attribute — a partly-selected page has to be
                  // set on the DOM node, or the box reads as "nothing selected" and an
                  // editor clears a selection they meant to extend.
                  if (node) node.indeterminate = someOnPageSelected;
                }}
                onChange={() => setMany(paged, !allOnPageSelected)}
                aria-label={
                  allOnPageSelected
                    ? "Deselect all rows on this page"
                    : "Select all rows on this page"
                }
              />
            </th>
            <th className="p-2">Preview</th>
            <th className="p-2">Filename</th>
            <th className="p-2">Alt text (marker stripped)</th>
            <th className="p-2">Marker</th>
            <th className="p-2">Badge position</th>
          </tr>
        </thead>
        <tbody>
          {paged.map((row) => (
            <AiImagesRow
              key={row.id}
              row={row}
              selected={selected.has(row.id)}
              siteBadgePosition={data.badgePosition}
              badgePosition={data.assetBadgePositions[row.id] ?? null}
              pendingMarker={
                writing?.scope === "marker" && writing.assetId === row.id
              }
              pendingPosition={
                writing?.scope === "position" && writing.assetId === row.id
              }
              locked={isWriting}
              onToggle={toggle}
              onPreview={setPreview}
              onMarker={(id, next) => void saveRowMarker(id, next)}
              onBadgePosition={(id, position) =>
                void saveAssetPosition(id, position)
              }
            />
          ))}
        </tbody>
      </table>

      {visible.length === 0 && (
        <p className="p-4 text-sm text-[var(--admin-text-muted)]">
          No assets match this view.
        </p>
      )}

      {visible.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-[var(--admin-text-muted)]">
          <span>
            {pageStart + 1}–{pageStart + paged.length} of {visible.length}
          </span>
          {pageCount > 1 && (
            <>
              <AdminButton
                disabled={currentPage === 0}
                onClick={() => setPage(currentPage - 1)}
              >
                Previous
              </AdminButton>
              <span>
                Page {currentPage + 1} of {pageCount}
              </span>
              <AdminButton
                disabled={currentPage >= pageCount - 1}
                onClick={() => setPage(currentPage + 1)}
              >
                Next
              </AdminButton>
            </>
          )}
          <AdminButton onClick={() => setMany(paged, !allOnPageSelected)}>
            {allOnPageSelected
              ? "Deselect page"
              : `Select page (${paged.length})`}
          </AdminButton>

          {pageCount > 1 && (
            <AdminButton onClick={() => setMany(visible, true)}>
              Select all {visible.length} results
            </AdminButton>
          )}

          {selected.size > 0 && (
            <>
              <span>{selected.size} selected (selection spans pages)</span>
              <AdminButton onClick={() => setSelected(new Set())}>
                Clear selection
              </AdminButton>
            </>
          )}
        </div>
      )}
      {preview && (
        <AiImagePreview
          row={preview}
          spaceId={data.spaceId}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
};

export default AiImagesConfig;
